// src/entities/payment/model/stripeTypes.ts

export interface StripeOnboardingStatus {
  stripeAccountId: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  requirementsCurrentlyDue: string[];
  disabledReason: string | null;
}

export interface StripeOnboardResponse {
  accountLinkUrl: string;
}

export interface StripeDashboardLinkResponse {
  loginLinkUrl: string;
}

export interface StripeCheckoutResponse {
  checkoutUrl: string;
  transactionId: string; // internal CUID — never put in URLs
}

export interface StripeSubscriptionCheckoutResponse {
  /**
   * Stripe-hosted checkout URL. The BE returns `null` (or omits the
   * field) for returning customers who already have a saved payment
   * method — in that case the BE charges the saved card directly and
   * the FE skips the redirect.
   */
  checkoutUrl: string | null;
}

export interface StripeRefundResponse {
  id: string;
  providerRefundId: string;
  amount: number;
  currency: string;
  status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'CANCELED';
  reason: string;
}

export type StripeOnboardingState =
  | 'NOT_STARTED'
  | 'ENABLED_NOT_ONBOARDED'
  | 'ONBOARDING_IN_PROGRESS'
  | 'NEEDS_DOCS'
  | 'RESTRICTED'
  | 'ACTIVE';

/**
 * Derives the UI state from the raw API data.
 * isEnabled = merchant has enabled Stripe for this store.
 * status = result of GET /merchant/payments/:id/status (null if not yet fetched).
 */
export function resolveStripeOnboardingState(
  isEnabled: boolean,
  status: StripeOnboardingStatus | null,
): StripeOnboardingState {
  if (!isEnabled) return 'NOT_STARTED';
  if (!status || !status.stripeAccountId) return 'ENABLED_NOT_ONBOARDED';
  if (status.disabledReason) return 'RESTRICTED';
  if (status.requirementsCurrentlyDue.length > 0) return 'NEEDS_DOCS';
  if (status.chargesEnabled && status.payoutsEnabled) return 'ACTIVE';
  return 'ONBOARDING_IN_PROGRESS';
}

export type StripeBadge = 'active' | 'not-configured' | 'disabled';

/**
 * Picks the provider-card badge variant for Stripe.
 *
 * Mirrors the Fonepay/COD grammar so the badge reflects the toggle once
 * Stripe is connected — NOT the onboarding state. The previous logic
 * resolved to "not-configured" whenever `!isEnabled` (i.e. the toggle was
 * off), which made the badge say "Not configured" even after the Stripe
 * account had been linked. The granular Stripe states (NEEDS_DOCS,
 * RESTRICTED, ENABLED_NOT_ONBOARDED, ONBOARDING_IN_PROGRESS) continue to
 * drive the body alerts and CTAs separately.
 */
export function resolveStripeBadge(
  state: StripeOnboardingState,
  isConnected: boolean,
): StripeBadge {
  if (state === 'ACTIVE') return 'active';
  if (isConnected) return 'disabled';
  return 'not-configured';
}
